import { useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/ScrollReveal';
import { Building2, Percent, CalendarClock, UserCog, ArrowRight } from 'lucide-react';
import { getBase44 } from '@/api/base44Lazy';
import { useToast } from '@/components/ui/use-toast';
import { markLeadCaptured } from '@/lib/leadCapture';

const BENEFITS = [
  { icon: Percent, title: 'Trade Pricing', desc: 'Exclusive discounted rates for letting agents managing multiple properties.' },
  { icon: CalendarClock, title: 'Priority Same-Day Booking', desc: 'Same-day appointment slots and flexible scheduling across your portfolio.' },
  { icon: UserCog, title: 'Dedicated Account Manager', desc: 'One point of contact who understands your portfolio and requirements.' },
  { icon: Building2, title: 'Monthly Invoicing', desc: 'Set up a payment agreement for single monthly invoicing with itemised breakdowns across all properties.' },
];

export default function EstateAgentsSection({ showLettingAgentLink = false }) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', agency: '', portfolio_size: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const base44 = await getBase44();
      await base44.entities.QuoteRequest.create({
        customer_name: form.name,
        email: form.email,
        phone: form.phone,
        customer_type: 'agent',
        is_vip: true,
        property_address: `${form.agency}${form.portfolio_size ? ` (${form.portfolio_size} properties)` : ''}${form.message ? ` — ${form.message}` : ''}`,
        postcode: '',
        service_ids: [],
        bedrooms: 0,
      });
      markLeadCaptured();
      toast({ title: 'Enquiry sent', description: "We'll be in touch within 24 hours to discuss trade pricing." });
      setForm({ name: '', email: '', phone: '', agency: '', portfolio_size: '', message: '' });
    } catch (err) {
      toast({ title: 'Error', description: 'Something went wrong. Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-white/20 bg-white/5 text-white text-sm placeholder:text-white/40 focus:border-orange outline-none transition-colors';

  return (
    <section className="pt-16 pb-0 md:pt-32 bg-navy text-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <ScrollReveal>
          <p className="caps-label text-orange mb-4">For Estate & Letting Agents</p>
          <h2 className="font-display font-extrabold mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Trade pricing for high-volume agents
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mb-16">
            Managing a portfolio? Get preferential rates, priority scheduling, and a dedicated account manager — built around how agents actually work.
            {showLettingAgentLink && (
              <>
                {' '}
                <Link to="/letting-agent-inventories" className="text-orange font-semibold underline hover:text-white transition-colors">Inventory services for letting agents</Link>
              </>
            )}
          </p>
        </ScrollReveal>
        <div className="grid lg:grid-cols-2 gap-16">
          <ScrollReveal>
            <div className="grid sm:grid-cols-2 gap-6">
              {BENEFITS.map(benefit => (
                <div key={benefit.title} className="border border-white/10 p-8 hover:border-orange/50 transition-colors">
                  <benefit.icon size={28} className="text-orange mb-4" />
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input type="text" required placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} />
                <input type="text" required placeholder="Agency name" value={form.agency} onChange={e => setForm({ ...form, agency: e.target.value })} className={inputClass} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input type="email" required placeholder="Email address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} />
                <input type="tel" placeholder="Phone number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} />
              </div>
              <input type="text" placeholder="How many properties in your portfolio?" value={form.portfolio_size} onChange={e => setForm({ ...form, portfolio_size: e.target.value })} className={inputClass} />
              <textarea rows={3} placeholder="Tell us about your needs (optional)" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className={`${inputClass} resize-none`} />
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-8 py-4 bg-orange text-navy font-semibold rounded-full hover:bg-orange-dark transition-colors disabled:opacity-50">
                {submitting ? 'Sending...' : 'Request Trade Pricing'}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}